import React, { useMemo } from 'react';
import { processMessageContent, ParsedCitation } from '@/utils/citationUtils';
import { splitContentByCharts } from '@/utils/chartParser';
import { InlineChatProposal } from './InlineChatProposal';
import { OrgainChart } from './OrgainChart';
import { CitationChip, CitationChipLight } from './CitationChip';

interface MessageContentProps {
  /** Raw message content that may contain citations */
  content: string;
  /** Whether this is a dark-themed message bubble (assistant) */
  isDark?: boolean;
  /** Additional className for the container */
  className?: string;
  /** KB sources array - used to show document names in citation tooltips */
  sources?: Array<{ title?: string; file_name?: string }>;
}

/**
 * MessageContent - Renders message text with ChatGPT-style citations
 *
 * Features:
 * - Parses inline citations from various formats
 * - Replaces citations with numbered superscript markers
 * - Shows a "Sources" section at the bottom with clickable chips
 * - Properly renders markdown formatting (headers, bold, bullets, etc.)
 * - Removes redundant "Key Documents" sections (since we show Sources)
 * - All links open in new tabs
 */
export const MessageContent: React.FC<MessageContentProps> = ({
  content,
  isDark = true,
  className = '',
  sources,
}) => {
  // Parse citations and process content
  const { processedText, citations } = useMemo(() => {
    return processMessageContent(content);
  }, [content]);

  // Clean up content - remove redundant "Key Documents/Resources" sections
  const cleanedText = useMemo(() => {
    let text = processedText;

    // Remove "Key Documents/Resources" sections entirely
    // Pattern: matches "---" followed by "## Key Documents" or "## Key Resources" section
    // This often appears at the end with [1], [2], [3] references
    text = text.replace(/---\s*\n+##\s*Key\s+(Documents|Resources)[^\n]*\n[\s\S]*?(?=---|$)/gi, '');

    // Also remove standalone "Key Documents" sections without the leading ---
    text = text.replace(/##\s*Key\s+(Documents|Resources)[^\n]*\n[\s\S]*?(?=##|---|$)/gi, '');

    // Remove orphaned reference lists like "_ [1]\n_ [2]" or "- [1]\n- [2]"
    text = text.replace(/(?:^|\n)[\s]*[-_*]\s*\[\d+\]\s*(?:\n|$)/gm, '\n');

    // Remove trailing horizontal rules
    text = text.replace(/\n---\s*$/g, '');

    // Clean up multiple consecutive blank lines
    text = text.replace(/\n{3,}/g, '\n\n');

    return text.trim();
  }, [processedText]);

  // Split content into text and chart segments
  const segments = useMemo(() => splitContentByCharts(cleanedText), [cleanedText]);

  // Render text with proper formatting
  const renderFormattedText = (text: string) => {
    // Split by newlines to handle paragraphs and block elements
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let currentIndex = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();

      // Skip empty lines but add spacing
      if (!trimmedLine) {
        elements.push(<div key={`space-${currentIndex++}`} className="h-2" />);
        continue;
      }

      // Check for horizontal rule (---, ***, ___) but NOT table separator rows like |---|---|
      if (/^[-*_]{3,}$/.test(trimmedLine)) {
        elements.push(
          <hr
            key={`hr-${currentIndex++}`}
            className={`my-4 border-t ${isDark ? 'border-white/20' : 'border-gray-200'}`}
          />
        );
        continue;
      }

      // Check for markdown table start (line starts with |)
      if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
        // Collect all consecutive table lines
        const tableLines: string[] = [trimmedLine];
        while (i + 1 < lines.length) {
          const nextTrimmed = lines[i + 1].trim();
          if (nextTrimmed.startsWith('|') && nextTrimmed.endsWith('|')) {
            tableLines.push(nextTrimmed);
            i++;
          } else {
            break;
          }
        }

        // Parse the table
        if (tableLines.length >= 2) {
          const parseCells = (row: string) =>
            row.split('|').slice(1, -1).map(c => c.trim());

          const headerCells = parseCells(tableLines[0]);

          // Check if second line is a separator (|---|---|)
          const isSeparator = (row: string) =>
            parseCells(row).every(c => /^[:\-]+$/.test(c));

          let dataStartIndex = 1;
          if (isSeparator(tableLines[1])) {
            dataStartIndex = 2;
          }

          const dataRows = tableLines.slice(dataStartIndex).filter(r => !isSeparator(r));

          elements.push(
            <div key={`table-${currentIndex++}`} className="my-3 overflow-x-auto max-w-full rounded-lg border border-white/10">
              <table className={`min-w-0 text-sm ${isDark ? 'text-white/90' : 'text-gray-800'}`}>
                <thead>
                  <tr className={isDark ? 'bg-white/10' : 'bg-gray-100'}>
                    {headerCells.map((cell, ci) => (
                      <th
                        key={ci}
                        className={`px-3 py-2 text-left font-semibold whitespace-nowrap border-b ${
                          isDark ? 'border-white/15' : 'border-gray-200'
                        }`}
                      >
                        {processInlineFormatting(cell, isDark)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dataRows.map((row, ri) => {
                    const cells = parseCells(row);
                    return (
                      <tr
                        key={ri}
                        className={`${
                          ri % 2 === 0
                            ? (isDark ? 'bg-white/[0.03]' : 'bg-white')
                            : (isDark ? 'bg-white/[0.06]' : 'bg-gray-50')
                        } hover:${isDark ? 'bg-white/10' : 'bg-gray-100'} transition-colors`}
                      >
                        {headerCells.map((_, ci) => (
                          <td
                            key={ci}
                            className={`px-3 py-1.5 border-b ${
                              isDark ? 'border-white/8' : 'border-gray-100'
                            }`}
                          >
                            {processInlineFormatting(cells[ci] || '', isDark)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
          continue;
        }
      }

      // Check for headers (# ## ### #### ##### ######)
      const headerMatch = trimmedLine.match(/^(#{1,6})\s+(.*)$/);
      if (headerMatch) {
        const level = headerMatch[1].length;
        const headerText = headerMatch[2];
        const HeaderTag = `h${level}` as keyof JSX.IntrinsicElements;

        // Define header styles based on level
        const headerStyles: Record<number, string> = {
          1: 'text-xl font-bold mt-4 mb-2',
          2: 'text-lg font-bold mt-3 mb-2',
          3: 'text-base font-semibold mt-3 mb-1',
          4: 'text-sm font-semibold mt-2 mb-1',
          5: 'text-sm font-medium mt-2 mb-1',
          6: 'text-xs font-medium mt-2 mb-1',
        };

        elements.push(
          <HeaderTag
            key={`header-${currentIndex++}`}
            className={headerStyles[level] || headerStyles[3]}
          >
            {processInlineFormatting(headerText, isDark, sources)}
          </HeaderTag>
        );
        continue;
      }

      // Process the line for various formatting
      const processedLine = processLine(line, isDark, sources);

      elements.push(
        <React.Fragment key={`line-${currentIndex++}`}>
          {processedLine}
        </React.Fragment>
      );
    }

    return elements;
  };

  return (
    <div className={`message-content ${className} min-w-0`}>
      {/* Main message text with inline charts */}
      <div className="space-y-0">
        {segments.map((segment, i) => {
          if (segment.type === 'proposal') {
            return <InlineChatProposal key={`proposal-${i}`} spec={segment.spec} />;
          }
          if (segment.type === 'chart') {
            return <OrgainChart key={`chart-${i}`} spec={segment.spec} />;
          }
          return (
            <React.Fragment key={`text-${i}`}>
              {renderFormattedText(segment.content)}
            </React.Fragment>
          );
        })}
      </div>

      {/* Sources section - only show if we have citations */}
      {citations.length > 0 && (
        <div className={`
          mt-4 pt-3
          ${isDark ? 'border-t border-white/20' : 'border-t border-gray-200'}
        `}>
          <div className={`
            text-xs font-semibold mb-2
            ${isDark ? 'text-white/70' : 'text-gray-500'}
          `}>
            Sources
          </div>
          <div className="flex flex-wrap gap-2">
            {citations.map((citation) => {
              const Chip = isDark ? CitationChip : CitationChipLight;
              return (
                <Chip
                  key={citation.index}
                  index={citation.index}
                  displayName={citation.displayName}
                  url={citation.url}
                  compact
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Process a single line of text, handling markdown formatting and citation markers
 */
function processLine(
  line: string,
  isDark: boolean,
  sources?: Array<{ title?: string; file_name?: string }>,
): React.ReactNode {
  const trimmedLine = line.trim();

  // Check for bullet points (-, *, or unicode bullet)
  const bulletMatch = trimmedLine.match(/^([•\-*])\s+(.*)$/);
  if (bulletMatch) {
    const [, , content] = bulletMatch;
    return (
      <div className="flex items-start gap-2 ml-2 my-1">
        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${isDark ? 'bg-white/60' : 'bg-gray-400'}`} />
        <span className="flex-1">{processInlineFormatting(content, isDark, sources)}</span>
      </div>
    );
  }

  // Check for numbered lists
  const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.*)$/);
  if (numberedMatch) {
    const [, number, content] = numberedMatch;
    return (
      <div className="flex items-start gap-2 my-1">
        <span className={`font-medium min-w-[1.5em] ${isDark ? 'text-white/80' : 'text-gray-600'}`}>{number}.</span>
        <span className="flex-1">{processInlineFormatting(content, isDark, sources)}</span>
      </div>
    );
  }

  // Regular line - just process inline formatting
  return <div className="my-0.5">{processInlineFormatting(trimmedLine, isDark, sources)}</div>;
}

/**
 * Process inline formatting: bold, italic, code, and citation markers
 */
function processInlineFormatting(text: string, isDark: boolean, sources?: Array<{ title?: string; file_name?: string }>): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let currentIndex = 0;
  let keyIndex = 0;

  // Combined pattern for various inline elements
  // Matches: **bold**, *italic*, `code`, [n] citations, and [text](url) links
  const combinedPattern = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[(\d+)\]|\[([^\]]+)\]\(([^)]+)\))/g;
  let match;

  while ((match = combinedPattern.exec(text)) !== null) {
    // Add text before the match
    if (match.index > currentIndex) {
      parts.push(text.slice(currentIndex, match.index));
    }

    const fullMatch = match[0];

    // Check what type of match this is
    if (fullMatch.startsWith('**') && fullMatch.endsWith('**')) {
      // Bold text
      const boldText = fullMatch.slice(2, -2);
      parts.push(<strong key={`bold-${keyIndex++}`}>{boldText}</strong>);
    } else if (fullMatch.startsWith('*') && fullMatch.endsWith('*') && !fullMatch.startsWith('**')) {
      // Italic text (single asterisk)
      const italicText = fullMatch.slice(1, -1);
      parts.push(<em key={`italic-${keyIndex++}`}>{italicText}</em>);
    } else if (fullMatch.startsWith('`') && fullMatch.endsWith('`')) {
      // Inline code
      const codeText = fullMatch.slice(1, -1);
      parts.push(
        <code
          key={`code-${keyIndex++}`}
          className={`px-1.5 py-0.5 rounded text-sm font-mono ${
            isDark ? 'bg-white/10 text-white/90' : 'bg-gray-100 text-gray-800'
          }`}
        >
          {codeText}
        </code>
      );
    } else if (match[2]) {
      // Citation marker [n]
      const citationNum = match[2];
      const sourceIndex = parseInt(citationNum, 10) - 1;
      const source = sources?.[sourceIndex];
      const tooltipName = source?.title || source?.file_name || `Citation ${citationNum}`;
      parts.push(
        <sup
          key={`cite-${keyIndex++}`}
          className={`
            citation-marker
            text-xs font-semibold
            ${isDark ? 'text-blue-300' : 'text-blue-600'}
            cursor-default
          `}
          title={tooltipName}
        >
          [{citationNum}]
        </sup>
      );
    } else if (match[3] && match[4]) {
      // Regular markdown link [text](url) that wasn't converted to citation
      const linkText = match[3];
      const linkUrl = match[4];
      parts.push(
        <a
          key={`link-${keyIndex++}`}
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`
            underline decoration-1 underline-offset-2
            ${isDark ? 'text-blue-300 hover:text-blue-200' : 'text-blue-600 hover:text-blue-500'}
          `}
        >
          {linkText}
        </a>
      );
    }

    currentIndex = match.index + fullMatch.length;
  }

  // Add any remaining text
  if (currentIndex < text.length) {
    parts.push(text.slice(currentIndex));
  }

  return parts.length > 0 ? parts : text;
}

export default MessageContent;
